import {
    type NormalizedState,
    initNormalizedState,
    normalizedStateToArray,
} from '~/utils/normalized-state'
import { sleep } from '~common/utils/sleep'
import type {
    AsyncQueueOptions,
    AsyncQueueStats,
    QueueItem,
    PersistedQueueState,
} from './types'

type InternalOptions<T, R> = Pick<
    AsyncQueueOptions<T, R>,
    'statePersister' | 'onBeforeItemProcess'
> &
    Required<
        Omit<AsyncQueueOptions<T, R>, 'statePersister' | 'onBeforeItemProcess'>
    >

export class AsyncQueue<T, R = void> {
    private pendingPromises: Map<
        string,
        {
            resolve: (value: R | null) => void
            reject: (error: Error) => void
        }
    > = new Map()

    private stats: AsyncQueueStats = {
        status: 'idle',
        queuedItems: 0,
        processingItems: 0,
        totalProcessed: 0,
        totalFailed: 0,
    }

    private options: InternalOptions<T, R>
    private initializationPromise: Promise<void>
    private persistStateWriteQueue: PersistedQueueState<T>[] = []
    private persistencePromise: Promise<void> | null = null
    private activeControllers = new Set<AbortController>()
    private items: NormalizedState<QueueItem<T>> = initNormalizedState()
    private workerPool: Promise<void>[] = []

    constructor(
        private processor: (item: T, signal: AbortSignal) => Promise<R>,
        options: AsyncQueueOptions<T, R>,
    ) {
        this.options = {
            maxQueueSize: options.maxQueueSize ?? Infinity,
            maxConcurrency: options.maxConcurrency ?? 1,
            autoStart: options.autoStart ?? true,
            startPaused: options.startPaused ?? false,
            queueName: options.queueName,
            statePersister: options.statePersister,
            onBeforeItemProcess: options.onBeforeItemProcess,
            onItemProcessed: options.onItemProcessed ?? (() => {}),
            onItemError: options.onItemError ?? (() => {}),
            onQueueEmpty: options.onQueueEmpty ?? (() => {}),
            maxRetries: options.maxRetries ?? 1,
            retriesExceededBehavior:
                options.retriesExceededBehavior ?? 'remove',
        }

        this.initializationPromise = this.initializeQueue()
    }

    private async initializeQueue(): Promise<void> {
        if (this.options.statePersister) {
            await this.attemptToHydrateQueue()
        }

        if (this.options.startPaused) {
            this.stats.status = 'paused'
        }
    }

    private calculateQueuedItems(): number {
        return normalizedStateToArray(this.items).filter(
            (i) =>
                i.status === 'queued' ||
                (i.status === 'error' &&
                    i.retryCount < this.options.maxRetries),
        ).length
    }

    /**
     * Add an item to the queue for processing
     * @param item The item to process
     * @param id ID for persistence
     * @param hasPriority Whether the item should be prioritized
     * @returns Promise that resolves when the item is processed. `null` means the item errored at some point.
     */
    async enqueue(
        item: T,
        id: string,
        hasPriority: boolean,
    ): Promise<R | null> {
        if (this.stats.queuedItems >= this.options.maxQueueSize) {
            throw new Error(
                `Queue is full (max size: ${this.options.maxQueueSize})`,
            )
        }

        if (this.items.byId[id]) {
            return null
        }

        // Pre-create the promise to ensure it's registered before any async operations
        let resolveFn: (value: R | null) => void = () => {}
        let rejectFn: (error: Error) => void = () => {}

        const promise = new Promise<R | null>((resolve, reject) => {
            resolveFn = resolve
            rejectFn = reject
        })
        this.pendingPromises.set(id, { resolve: resolveFn, reject: rejectFn })

        try {
            const queueItem: QueueItem<T> = {
                id,
                item,
                enqueuedAt: Date.now(),
                status: 'queued',
                hasPriority,
                retryCount: 0,
            }

            this.items.allIds.push(id)
            this.items.byId[id] = queueItem
            this.stats.queuedItems = this.calculateQueuedItems()

            this.persistQueueState()

            if (this.options.autoStart && this.stats.status !== 'paused') {
                this.start()
            }

            return promise
        } catch (error) {
            this.pendingPromises.delete(id)
            throw error
        }
    }

    start(): void {
        if (this.stats.status === 'processing' && this.workerPool.length > 0) {
            return
        }
        this.stats.status = 'processing'

        // Reset any items that were stuck in 'processing' back to 'queued'
        let hasUpdates = false
        for (const item of normalizedStateToArray(this.items)) {
            if (item.status === 'processing') {
                item.status = 'queued'
                hasUpdates = true
            }
        }

        if (hasUpdates) {
            this.stats.queuedItems = this.calculateQueuedItems()
            this.persistQueueState()
        }

        // Clear old workers before relaunching
        this.workerPool = []

        // Launch worker pool
        for (let i = 0; i < this.options.maxConcurrency; i++) {
            const worker = this.createWorker()
            this.workerPool.push(worker)
        }
    }

    /**
     * Pause processing new items (items currently being processed will complete)
     */
    pause(): void {
        this.stats.status = 'paused'
        // Workers will exit loop on next iteration; current processes continue
        this.pendingPromises.forEach(({ resolve }) => {
            resolve(null)
        })
        this.pendingPromises.clear()
    }

    /**
     * Resume processing items in the queue
     */
    resume(): void {
        if (this.stats.status !== 'processing') {
            this.start()
            return
        }

        // Reset stuck processing items
        let hasUpdates = false
        for (const item of normalizedStateToArray(this.items)) {
            if (item.status === 'processing') {
                item.status = 'queued'
                hasUpdates = true
            }
        }
        if (hasUpdates) {
            this.persistQueueState()
        }

        this.stats.status = 'processing'
        // Relaunch workers (they exited during pause)
        this.workerPool = []
        for (let i = 0; i < this.options.maxConcurrency; i++) {
            this.workerPool.push(this.createWorker())
        }

        this.persistQueueState()
    }

    /**
     * Stop processing new items and abort any currently running ones
     */
    stop(options?: { skipTaskAbort: boolean }): void {
        this.stats.status = 'idle'
        this.pendingPromises.forEach(({ resolve }) => {
            resolve(null)
        })
        this.pendingPromises.clear()
        this.workerPool = []

        if (!options?.skipTaskAbort) {
            this.activeControllers.forEach((controller) => controller.abort())
            this.activeControllers.clear()
        }
    }

    /**
     * Wait for all items in the queue to be processed
     */
    async waitForCompletion(): Promise<void> {
        while (this.stats.queuedItems > 0 || this.stats.processingItems > 0) {
            // Small delay to avoid busy waiting
            await sleep(10)
        }
    }

    /**
     * Clear all queued items (items currently being processed will complete)
     */
    clear(): void {
        // Reject all pending promises
        this.pendingPromises.forEach(({ reject }) => {
            reject(new Error('Queue cleared'))
        })
        this.pendingPromises.clear()
        this.stats.queuedItems = 0
        this.items = initNormalizedState()
        this.persistQueueState()
    }

    getStats(): AsyncQueueStats {
        return { ...this.stats }
    }

    get length(): number {
        return this.stats.queuedItems
    }

    isQueueEmpty(): boolean {
        return this.items.allIds.length === 0
    }

    private createWorker = (): Promise<void> =>
        (async () => {
            while (
                this.stats.status === 'processing' &&
                this.items.allIds.length > 0
            ) {
                try {
                    const queueItem = this.getNextItem()
                    if (!queueItem) {
                        const activeItems = this.calculateQueuedItems()

                        // If no active items (queued or retryable) and no processing items, we are done.
                        if (
                            activeItems === 0 &&
                            this.stats.processingItems === 0
                        ) {
                            this.stats.status = 'idle'
                            this.options.onQueueEmpty()
                            break
                        }

                        // Otherwise, wait and retry (e.g. waiting for processing items to finish or retries)
                        await sleep(100)
                        continue
                    }

                    await this.processSingleItem(queueItem)
                    // Yield to event loop to allow other operations (like stop) to process
                    await Promise.resolve()
                } catch (error) {
                    // Don't exit loop: retry polling after delay
                    await sleep(100)
                }
            }
        })()

    private getNextItem(): QueueItem<T> | null {
        const itemsArray = normalizedStateToArray(this.items)

        // Priority 1: Queued Priority Items
        let nextItem = itemsArray
            .filter((i) => i.status === 'queued' && i.hasPriority)
            .sort((a, b) => a.enqueuedAt - b.enqueuedAt)[0]

        // Priority 2: Queued Non-Priority Items
        if (!nextItem) {
            nextItem = itemsArray
                .filter((i) => i.status === 'queued' && !i.hasPriority)
                .sort((a, b) => a.enqueuedAt - b.enqueuedAt)[0]
        }

        // Priority 3: Retryable Errors
        if (!nextItem) {
            nextItem = itemsArray
                .filter(
                    (i) =>
                        i.status === 'error' &&
                        i.retryCount < this.options.maxRetries,
                )
                .sort((a, b) => a.enqueuedAt - b.enqueuedAt)[0]

            if (nextItem) {
                // Reset error state for retry
                nextItem.errorMessage = undefined
                // Don't persist here, will persist when marked processing below
            }
        }

        if (!nextItem) {
            return null
        }

        // Mark as processing
        nextItem.status = 'processing'
        this.stats.processingItems++
        // Recalculate queued items count immediately upon selection
        this.stats.queuedItems = this.calculateQueuedItems()

        this.persistQueueState()

        return nextItem
    }

    // Process single item, delete only on success (reset to queued on error for retry)
    private async processSingleItem(queueItem: QueueItem<T>): Promise<void> {
        const { id, item, retryCount } = queueItem
        const controller = new AbortController()
        const signal = controller.signal
        this.activeControllers.add(controller)

        const handlers = this.pendingPromises.get(id)
        let result: R | null = null
        let successfullyProcessed = false

        try {
            await this.options.onBeforeItemProcess?.(item)
        } catch {
            // If precondition check errors, we must:
            // 1. Stop the queue to prevent further processing (as per contract)
            // 2. Reset the current item's status to 'queued' so it isn't lost
            // 3. Correctly decrement processingItems count

            if (this.items.byId[id]) {
                this.items.byId[id].status = 'queued'
            }

            this.stats.processingItems--
            this.stats.queuedItems = this.calculateQueuedItems()

            this.persistQueueState()
            this.stop()
            return
        }

        try {
            result = await this.processor(item, signal)
            successfullyProcessed = true
        } catch (error) {
            if (error instanceof AbortError) {
                queueItem.status = 'queued'
                queueItem.errorMessage = undefined
                handlers?.resolve(null)
            } else if (error instanceof SkipRetryError) {
                // Permanently fail/remove
                this.removeItem(id)
                this.stats.totalFailed++
                handlers?.resolve(null)
            } else {
                const errorObj =
                    error instanceof Error ? error : new Error(String(error))
                const nextRetry = retryCount + 1
                console.warn(
                    `[AsyncQueue ${this.options.queueName}] Item ${id} failed (retry attempt ${nextRetry}/${this.options.maxRetries}):`,
                    errorObj.message,
                )

                if (nextRetry < this.options.maxRetries) {
                    queueItem.status = 'error'
                    queueItem.errorMessage = errorObj.message
                    queueItem.retryCount++
                } else if (this.options.retriesExceededBehavior === 'remove') {
                    console.error(
                        `[AsyncQueue ${this.options.queueName}] Item ${id} exceeded max retries (${this.options.maxRetries}), marking as permanently failed`,
                    )
                    this.removeItem(id)
                } else if (
                    this.options.retriesExceededBehavior === 'reenqueue'
                ) {
                    queueItem.status = 'queued'
                    queueItem.errorMessage = undefined
                    queueItem.retryCount = 0
                    queueItem.enqueuedAt = Date.now()
                    queueItem.hasPriority = false
                }

                this.stats.totalFailed++
                this.options.onItemError(item, errorObj)
                handlers?.resolve(null)
            }
        } finally {
            this.activeControllers.delete(controller)
            if (successfullyProcessed) {
                this.options.onItemProcessed(item, result)
                this.removeItem(id)
                this.stats.totalProcessed++
                handlers?.resolve(result)
            }
            this.stats.processingItems--
            this.stats.queuedItems = this.calculateQueuedItems()

            this.pendingPromises.delete(id)

            // Check actual items count
            const activeItems = this.calculateQueuedItems()
            if (activeItems === 0 && this.stats.processingItems === 0) {
                this.stats.status = 'idle'
                this.options.onQueueEmpty()
            }
            this.persistQueueState()
        }
    }

    private removeItem(id: string): void {
        this.items.allIds = this.items.allIds.filter((i) => i !== id)
        delete this.items.byId[id]
    }

    async waitForPersistence(): Promise<void> {
        if (this.persistencePromise) {
            await this.persistencePromise
        }
    }

    private async processPersistence(): Promise<void> {
        if (!this.options.statePersister) {
            return
        }

        try {
            while (this.persistStateWriteQueue.length > 0) {
                const state = this.persistStateWriteQueue.shift()
                if (!state) {
                    break
                }
                await this.options.statePersister.persist(state)
            }
        } finally {
            this.persistencePromise = null // Clear when done
        }
    }

    /**
     * Persist the current queue state to disk
     */
    private persistQueueState(): void {
        if (!this.options.statePersister) {
            return
        }

        this.persistStateWriteQueue.push({
            version: 1,
            queuedItems: this.items.allIds.length,
            status: this.stats.status,
            totalProcessed: this.stats.totalProcessed,
            totalFailed: this.stats.totalFailed,
            items: normalizedStateToArray(this.items),
        })

        // Start processing if not already running
        if (!this.persistencePromise) {
            this.persistencePromise = this.processPersistence()
        }
    }

    private async attemptToHydrateQueue(): Promise<void> {
        if (!this.options.statePersister) {
            return
        }

        try {
            const state = await this.options.statePersister.hydrate()
            if (state == null) {
                return
            }

            // Restore cached count and statistics
            this.stats.status = state.status
            this.stats.queuedItems = state.queuedItems
            this.stats.totalProcessed = state.totalProcessed
            this.stats.totalFailed = state.totalFailed
            this.items = initNormalizedState({
                seedData: state.items ?? [],
                getId: (i) => i.id,
            })

            // Reset any processing items to queued on load
            let hasUpdates = false
            for (const item of normalizedStateToArray(this.items)) {
                if (item.status === 'processing') {
                    item.status = 'queued'
                    hasUpdates = true
                }
            }

            // Restore state
            if (state.status === 'processing') {
                this.stats.status = 'processing'
            } else if (state.status === 'paused') {
                this.stats.status = 'paused'
            } else {
                this.stats.status = 'idle'
            }

            if (hasUpdates) {
                this.persistQueueState()
            }
        } catch (error) {
            // If restoration fails, start with empty queue
            this.stats.status = 'idle'
            this.stats.queuedItems = 0
            this.items = initNormalizedState()
        }
    }

    async clearPersistence(): Promise<void> {
        if (this.options.statePersister) {
            await this.options.statePersister.clear()
        }
        this.items = initNormalizedState()
    }
}

export class AbortError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'AbortError'
        Object.setPrototypeOf(this, AbortError.prototype)
    }
}

export function assertNotAborted(signal?: AbortSignal): void {
    if (!signal?.aborted) {
        return
    }
    throw new AbortError('Task aborted')
}

/**
 * Throw this error to skip retrying an item in the AsyncQueue.
 */
export class SkipRetryError extends Error {
    constructor(message?: string) {
        super(message ?? 'Skip retry error')
        this.name = 'SkipRetryError'
        Object.setPrototypeOf(this, SkipRetryError.prototype)
    }
}
